import {
    ɵComponentType as ComponentType,
    ɵDirectiveType as DirectiveType,
    ElementRef,
    InjectableType,
    ɵɵdirectiveInject as directiveInject,
    ɵNG_COMP_DEF as NG_COMP_DEF,
    ɵNG_DIR_DEF as NG_DIR_DEF,
    Renderer2,
    TypeDecorator
} from '@angular/core';
import { TeamNames } from 'src/app/shared/enums/team-names.enum';

export const RENDERER_PROPS = [
    'createElement',
    'createComment',
    'createText',
    'destroyNode',
    'appendChild',
    'insertBefore',
    'removeChild',
    'selectRootElement',
    'parentNode',
    'nextSibling',
    'setAttribute',
    'removeAttribute',
    'addClass',
    'removeClass',
    'setStyle',
    'removeStyle',
    'setProperty',
    'setValue',
    'listen'
];
// This Record is used to pair component class names with their respective owners.
export const OWNED_OBJECTS: Record<string, TeamNames | TeamNames[]> = {};

/**
 * A Decorator for marking ownership of a component or directive.
 * When added to the component header, will append a team name to the error that is logged.
 * If Renderer2 and ElementRef are injected, will also add the nova-owner attribute with a value of ownerName.
 * @param ownerName The team name that owns the component or directive in question
 * @returns
 */
export function Owner(ownerName: TeamNames | TeamNames[]): TypeDecorator {
    return (type: any) => {
        decorateClass(type, ownerName);
    };
}

/**
 * Performs the appropriate decorations on the target given its type (pipe or component/directive)
 * @param type
 * @param ownerName
 * @returns
 */
function decorateClass<T>(
    type: DirectiveType<T> | ComponentType<T> | InjectableType<T>,
    ownerName: TeamNames | TeamNames[]
): void {
    registerClassWithOwnedComponents(type.name, ownerName);
    if (!isComponent(type) && !isDirective(type)) {
        return;
    }

    tryPatchFactory(type);

    if (!type.prototype.ngOnInit) {
        // The function below will create the onInit function and inject a renderer and element ref
        createOnInit(type);
    }
    type.prototype.ngOnInit = decorateNgOnInit(type.prototype.ngOnInit, ownerName, type.name);
}

function tryPatchFactory(type: any) {
    if (!type.ɵfac || typeof type.ɵfac !== 'function' || !canOverwriteFactory(type)) {
        return;
    }

    const originalFactory = type.ɵfac;

    type.ɵfac = function (...args: unknown[]) {
        const instance = originalFactory(...args);

        try {
            instance.__elementRef = directiveInject(ElementRef);
            instance.__renderer = directiveInject(Renderer2);
        } catch (e) {
            // do nothing. warning will be logged later.
        }

        return instance;
    };
}

// In router module unit tests, the component's factory is read-only, and setting type.ɵfac throws an exception
// This check is necessary to ensure the factory can be set before proceeding in tryPatchFactory
function canOverwriteFactory(type: any): boolean {
    const factoryDescriptor = Object.getOwnPropertyDescriptor(type, 'ɵfac');
    if (!factoryDescriptor || !factoryDescriptor.writable || typeof type.ɵfac !== 'function') {
        return false;
    }
    return true;
}

// Component type guard using Angular internal properties
export function isComponent<T>(target: any): target is ComponentType<T> {
    return !!target[NG_COMP_DEF] || target.prototype.NOVA_UNIT_TEST_OWNER_FLAG;
}

// Directive type guard using Angular internal properties
export function isDirective<T>(target: any): target is DirectiveType<T> {
    return !!target[NG_DIR_DEF];
}
export function createOnInit(type: any): void {
    type.prototype.ngOnInit = () => {};
}

export function registerClassWithOwnedComponents(typeName: string, ownerName: TeamNames | TeamNames[]) {
    OWNED_OBJECTS[typeName] = ownerName;
}

export function decorateNgOnInit(
    ngOnInit: (() => void) | null | undefined,
    ownerName: TeamNames | TeamNames[],
    typeName: string
) {
    return function (this: any) {
        const componentName = typeName;

        // Invoke the original `ngOnInit` if it exists
        ngOnInit && ngOnInit.call(this);

        const props = Object.keys(this);

        // Get the element reference and renderer to append the owner attribute to the HTML element
        const elementRef: ElementRef = this.__elementRef;
        const renderer: Renderer2 = this.__renderer;

        // If these services don't exist, do nothing.
        if (!elementRef || !renderer) {
            return;
        }

        // Modify the element after the view initializes
        if (Array.isArray(ownerName)) {
            renderer.setAttribute(elementRef.nativeElement, 'owned-by', ownerName.join(', '));
        } else {
            renderer.setAttribute(elementRef.nativeElement, 'owned-by', ownerName);
        }
    };
}
